import { Link } from '@tanstack/react-router';

import {
    KpiStatCard,
    type KpiStatDelta,
} from '@apps/web/components/admin/outreach_observability/kpi_stat_card';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { zeroFilledDailySeries } from '@apps/web/features/admin_agent_notes/model/heatmap_scale';
import { type AgentNotesSearch } from '@apps/web/features/admin_agent_notes/model/search_params';
import { MiniSparkline } from '@apps/web/features/admin_agent_notes/ui/mini_sparkline';
import { type AdminAgentNoteStatsResponse } from '@libraries/typescript/api_client';

// Failure-rate level that flags the Failed tile red; matches the dashed
// threshold on the failure-rate chart.
const FAILURE_FLAG_THRESHOLD_PCT = 2;

function formatHour(hour: number): string {
    if (hour === 0) {
        return '12 AM';
    }
    if (hour === 12) {
        return '12 PM';
    }
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function pct(numerator: number, denominator: number): number | null {
    return denominator > 0 ? (100 * numerator) / denominator : null;
}

/** Percent change vs the previous window ("prev ▲12%"). */
function countDelta(current: number, previous: number): KpiStatDelta {
    return {
        label: 'prev',
        pct: previous > 0 ? ((current - previous) / previous) * 100 : null,
        goodWhenUp: true,
    };
}

/** Percentage-point change of a rate vs the previous window. */
function rateDelta(
    currentPct: number | null,
    previousPct: number | null,
    goodWhenUp: boolean,
): KpiStatDelta {
    return {
        label: 'prev',
        pct:
            currentPct !== null && previousPct !== null
                ? currentPct - previousPct
                : null,
        goodWhenUp,
        format: 'points',
    };
}

export function NotesKpiStrip({
    stats,
    isLoading,
    buildNotesSearch,
}: {
    readonly stats: AdminAgentNoteStatsResponse | undefined;
    readonly isLoading: boolean;
    readonly buildNotesSearch: (
        status?: AdminAgentNoteStatsResponse['status_counts'][number]['status'],
    ) => AgentNotesSearch;
}) {
    if (isLoading || stats === undefined) {
        return (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {[0, 1, 2, 3, 4].map((index) => (
                    <Skeleton key={index} className="h-[132px]" />
                ))}
            </div>
        );
    }

    const dailySeries = zeroFilledDailySeries(
        stats.daily_counts,
        stats.window_start,
        stats.window_end,
    );
    const totals = dailySeries.map((entry) => entry.total);
    const failedSeries = dailySeries.map((entry) => entry.failed);
    const notesInWindow = totals.reduce((sum, value) => sum + value, 0);
    // Tolerate a stale API during rolling deploys (deltas render as "—"
    // until the API serving previous-window aggregates is live).
    const previous = stats.previous ?? {
        notes: 0,
        failed: 0,
        fallback: 0,
        routed: 0,
        applied: 0,
    };
    const fallbackCount =
        stats.status_counts.find((entry) => entry.status === 'fallback')
            ?.count ?? 0;
    const todayDelta = stats.notes_today - stats.notes_yesterday;
    const peakHour = stats.hourly_counts.reduce(
        (best, entry) => (entry.count > best.count ? entry : best),
        { hour: 0, count: 0 },
    );

    const failureRatePct = pct(stats.failed_in_window, notesInWindow);
    const fallbackRatePct = pct(fallbackCount, notesInWindow);
    const appliedRatePct = pct(stats.funnel.applied, stats.funnel.routed);
    const failureFlagged =
        failureRatePct !== null && failureRatePct > FAILURE_FLAG_THRESHOLD_PCT;

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Link to="/admin/agent-notes" search={buildNotesSearch()}>
                <KpiStatCard
                    label="Notes"
                    value={String(notesInWindow)}
                    sub={`${stats.notes_today} today (${todayDelta >= 0 ? '+' : ''}${todayDelta})`}
                    deltas={[countDelta(notesInWindow, previous.notes)]}
                    sparkline={<MiniSparkline values={totals} />}
                />
            </Link>
            <Link to="/admin/agent-notes" search={buildNotesSearch('failed')}>
                <KpiStatCard
                    label="Failed"
                    value={String(stats.failed_in_window)}
                    sub={
                        failureRatePct !== null
                            ? `${failureRatePct.toFixed(1)}% of notes`
                            : 'no notes in range'
                    }
                    flag={
                        failureFlagged
                            ? {
                                  level: 'red',
                                  description: `above ${FAILURE_FLAG_THRESHOLD_PCT}% threshold`,
                              }
                            : undefined
                    }
                    deltas={[
                        rateDelta(
                            failureRatePct,
                            pct(previous.failed, previous.notes),
                            false,
                        ),
                    ]}
                    sparkline={
                        stats.failed_in_window > 0 ? (
                            <MiniSparkline
                                values={failedSeries}
                                stroke="var(--destructive)"
                            />
                        ) : undefined
                    }
                />
            </Link>
            <Link to="/admin/agent-notes" search={buildNotesSearch('fallback')}>
                <KpiStatCard
                    label="Fallback rate"
                    value={
                        fallbackRatePct !== null
                            ? `${fallbackRatePct.toFixed(1)}%`
                            : '—'
                    }
                    sub={`${fallbackCount} to memory`}
                    deltas={[
                        rateDelta(
                            fallbackRatePct,
                            pct(previous.fallback, previous.notes),
                            false,
                        ),
                    ]}
                />
            </Link>
            <KpiStatCard
                label="Applied rate"
                value={
                    appliedRatePct !== null
                        ? `${appliedRatePct.toFixed(1)}%`
                        : '—'
                }
                sub={`${stats.funnel.applied} of ${stats.funnel.routed} routed`}
                deltas={[
                    rateDelta(
                        appliedRatePct,
                        pct(previous.applied, previous.routed),
                        true,
                    ),
                ]}
            />
            <KpiStatCard
                label="Peak hour"
                value={peakHour.count > 0 ? formatHour(peakHour.hour) : '—'}
                sub={
                    peakHour.count > 0
                        ? `${peakHour.count} notes`
                        : 'no notes in range'
                }
            />
        </div>
    );
}
